// Optional video virtual-background for Pula Calls.
// Uses MediaPipe Selfie Segmentation, loaded from CDN ONLY when a background is
// first enabled — so it never burdens people who don't use it (or low-end phones).
// All processing is on-device; the composited frames go straight into the P2P track.

const MP_SRC = "https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1/selfie_segmentation.js";
let mpLoading = null;

function loadMediaPipe() {
  if (window.SelfieSegmentation) return Promise.resolve();
  if (mpLoading) return mpLoading;
  mpLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = MP_SRC;
    s.crossOrigin = "anonymous";
    s.onload = () => (window.SelfieSegmentation ? resolve() : reject(new Error("mp missing")));
    s.onerror = () => reject(new Error("mp load failed"));
    document.head.appendChild(s);
  });
  return mpLoading;
}

export const BACKGROUNDS = [
  { key: "none", label: "None" },
  { key: "blur", label: "Blur" },
  { key: "kalahari", label: "Kalahari", url: "https://static.prod-images.emergentagent.com/jobs/c846ba40-1430-4ea2-a418-489b99072dd3/images/1bab656e9c6fee3919b36358f8911d1f51d9e607ffc32f3a96f2ac1f4003605c.jpeg" },
  { key: "savanna", label: "Savanna", url: "https://static.prod-images.emergentagent.com/jobs/c846ba40-1430-4ea2-a418-489b99072dd3/images/1086ae330cb182a84345559bc972acc00d37e623e63f735534fd834c6643c150.jpeg" },
];

export class VirtualBackground {
  constructor(inputStream) {
    this.input = inputStream;
    this.video = document.createElement("video");
    this.video.autoplay = true; this.video.playsInline = true; this.video.muted = true;
    this.video.srcObject = new MediaStream(inputStream.getVideoTracks());
    this.canvas = document.createElement("canvas");
    this.ctx = this.canvas.getContext("2d");
    this.mode = "blur";
    this.bgImg = null;
    this.running = false;
    this.seg = null;
    this.output = null;
  }

  async start(mode, url) {
    await loadMediaPipe();
    await this.video.play().catch(() => {});
    const track = this.input.getVideoTracks()[0];
    const s = track?.getSettings?.() || {};
    this.canvas.width = s.width || 640;
    this.canvas.height = s.height || 480;
    this.setBackground(mode, url);
    this.seg = new window.SelfieSegmentation({
      locateFile: (f) => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1/${f}`,
    });
    this.seg.setOptions({ modelSelection: 1 });
    this.seg.onResults((r) => this._draw(r));
    this.running = true;
    this._loop();
    this.output = this.canvas.captureStream(24);
    return this.output;
  }

  setBackground(mode, url) {
    this.mode = mode;
    if (url) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = url;
      this.bgImg = img;
    } else {
      this.bgImg = null;
    }
  }

  async _loop() {
    if (!this.running) return;
    try { if (this.video.readyState >= 2 && this.seg) await this.seg.send({ image: this.video }); } catch { /* ignore */ }
    if (this.running) setTimeout(() => this._loop(), 40); // ~24fps, gentle on CPU
  }

  _drawCover(img) {
    const { ctx, canvas } = this;
    const w = canvas.width, h = canvas.height;
    const ir = img.width / img.height, cr = w / h;
    let dw, dh, dx, dy;
    if (ir > cr) { dh = h; dw = h * ir; dx = (w - dw) / 2; dy = 0; }
    else { dw = w; dh = w / ir; dx = 0; dy = (h - dh) / 2; }
    ctx.drawImage(img, dx, dy, dw, dh);
  }

  _draw(results) {
    const { ctx, canvas } = this;
    const w = canvas.width, h = canvas.height;
    ctx.save();
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(results.segmentationMask, 0, 0, w, h);
    ctx.globalCompositeOperation = "source-in";
    ctx.drawImage(results.image, 0, 0, w, h);
    ctx.globalCompositeOperation = "destination-over";
    if (this.mode === "blur") {
      ctx.filter = "blur(12px)";
      ctx.drawImage(results.image, 0, 0, w, h);
      ctx.filter = "none";
    } else if (this.bgImg && this.bgImg.complete && this.bgImg.naturalWidth) {
      this._drawCover(this.bgImg);
    } else {
      ctx.fillStyle = "#0b3d2e";
      ctx.fillRect(0, 0, w, h);
    }
    ctx.restore();
  }

  stop() {
    this.running = false;
    try { this.seg?.close?.(); } catch { /* ignore */ }
    try { this.output?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    this.video.srcObject = null;
    this.output = null;
  }
}

// Scenic backdrops for the photo editor (served same-origin so the canvas never taints).
export const PHOTO_BACKGROUNDS = [
  { key: "none", label: "None" },
  { key: "blur", label: "Blur" },
  { key: "mountain", label: "🏔️ Mountain", url: "/backgrounds/mountain.jpg" },
  { key: "sea", label: "🌊 Beach", url: "/backgrounds/sea.jpg" },
  { key: "forest", label: "🌲 Forest", url: "/backgrounds/forest.jpg" },
  { key: "sunset", label: "🌅 Sunset", url: "/backgrounds/sunset.jpg" },
  { key: "city", label: "🌆 City", url: "/backgrounds/city.jpg" },
];

function _loadImg(src) {
  return new Promise((res, rej) => { const i = new Image(); i.crossOrigin = "anonymous"; i.onload = () => res(i); i.onerror = rej; i.src = src; });
}
function _cover(ctx, img, w, h) {
  const ir = img.width / img.height, cr = w / h;
  let dw, dh, dx, dy;
  if (ir > cr) { dh = h; dw = h * ir; dx = (w - dw) / 2; dy = 0; }
  else { dw = w; dh = w / ir; dx = 0; dy = (h - dh) / 2; }
  ctx.drawImage(img, dx, dy, dw, dh);
}

// Cut the person out of a still photo and place them on a scenic background (or blur).
// Returns a new JPEG File. Reuses the same on-device MediaPipe model as Pula Calls.
export async function applyPhotoBackground(file, mode, url) {
  await loadMediaPipe();
  const src = URL.createObjectURL(file);
  try {
    const img = await _loadImg(src);
    const scale = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.round(img.naturalWidth * scale), h = Math.round(img.naturalHeight * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    const seg = new window.SelfieSegmentation({ locateFile: (f) => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1/${f}` });
    seg.setOptions({ modelSelection: 1 });
    const results = await new Promise((resolve, reject) => {
      const to = setTimeout(() => reject(new Error("segmentation timeout")), 15000);
      seg.onResults((r) => { clearTimeout(to); resolve(r); });
      seg.send({ image: img });
    });
    const bg = mode !== "blur" && url ? await _loadImg(url).catch(() => null) : null;
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(results.segmentationMask, 0, 0, w, h);
    ctx.globalCompositeOperation = "source-in";
    ctx.drawImage(results.image, 0, 0, w, h);
    ctx.globalCompositeOperation = "destination-over";
    if (mode === "blur") { ctx.filter = "blur(14px)"; ctx.drawImage(results.image, 0, 0, w, h); ctx.filter = "none"; }
    else if (bg) { _cover(ctx, bg, w, h); }
    else { ctx.fillStyle = "#0b3d2e"; ctx.fillRect(0, 0, w, h); }
    ctx.globalCompositeOperation = "source-over";
    try { seg.close?.(); } catch { /* ignore */ }
    const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.88));
    return new File([blob], (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg", lastModified: Date.now() });
  } finally {
    URL.revokeObjectURL(src);
  }
}
